// Route table for the Vite + React Router build.
//
// Each staff view has its own layout route (FrontendLayout), and the portal
// has its own shell. The auth gate at `/` runs in routes/home.tsx. This file
// holds the layouts only: the routes under each layout come from the feature
// files that the Feature Manifest enables (`routesFor`, ADR-0027). A path that
// no enabled feature serves ends on the catch-all NotFound page of its layout.

import { Suspense, lazy, type ComponentType, type ReactElement } from 'react';
import { createBrowserRouter, type RouteObject } from 'react-router';
import { RootLayout } from './routes/root';
import { HomePage } from './routes/home';
import { RootErrorBoundary } from './routes/error';
import { DeskLayoutRoute } from './routes/desk-layout';
import { OpsLayoutRoute } from './routes/ops-layout';
import { ComplyLayoutRoute } from './routes/comply-layout';
import { ViewLayoutRoute } from './routes/view-layout';
import { AdminLayoutRoute } from './routes/admin-layout';
import { PortalLayoutRoute } from './routes/portal-layout';
import NotFoundPage from '@/components/layout/NotFoundPage';
import { FeatureRoute } from '@/components/layout/FeatureRoute';
import { routesFor, type Contributed } from '@/lib/feature-registry';
import type { AppRoute, RouteLayout } from '@/features/kit';

function Loading() {
    return <div className="p-8 text-body text-[rgb(var(--color-text-secondary))]">Loading…</div>;
}

function s(node: ReactElement) {
    return <Suspense fallback={<Loading />}>{node}</Suspense>;
}

/** One router entry for a feature route. The page chunk loads when the route renders. */
function toRouteObject(route: Contributed<AppRoute>): RouteObject {
    let node: ReactElement;
    if (route.lazy) {
        const Page = lazy(route.lazy as () => Promise<{ default: ComponentType }>);
        node = <Page />;
    } else {
        node = <>{route.element}</>;
    }
    // The build holds only enabled features. `FeatureRoute` adds the check of
    // the AI seat of the user, which the build cannot know.
    const element = s(<FeatureRoute id={route.feature}>{node}</FeatureRoute>);
    return route.path === '' ? { index: true, element } : { path: route.path, element };
}

/** The routes of a layout, then its catch-all. */
function childrenOf(layout: RouteLayout, inStaffShell: boolean): RouteObject[] {
    return [
        ...routesFor(layout).map(toRouteObject),
        { path: '*', element: <NotFoundPage inStaffShell={inStaffShell} /> },
    ];
}

/**
 * Where the application is mounted, from the build's own `base`.
 *
 * The bundle is served at the root everywhere that matters — CloudFront in
 * production, Vite in local development — and `BASE_URL` is `/` for both, so
 * this changes nothing there. The SCENARIO COPY is built with a base path
 * (`vite build --base=/review/dowgate/app/`), because it is shown inside a frame on
 * the reviewer's site, which serves it under a prefix. The router reads its own
 * `location.pathname`, so it must be told the prefix or it matches no route
 * under it.
 *
 * The trailing slash goes: React Router wants `/review/dowgate/app`, and `BASE_URL`
 * always ends in one.
 */
const basename = import.meta.env.BASE_URL.replace(/\/$/, '') || '/';

export const router = createBrowserRouter(
    [
        {
            path: '/',
            Component: RootLayout,
            ErrorBoundary: RootErrorBoundary,
            children: [
                { index: true, element: <HomePage /> },
                // The sign-in, the refusal page, the public status page and
                // the public client application. Each one sits under
                // RootLayout with no staff sidebar and no agent panel.
                ...routesFor('public').map(toRouteObject),
                { path: 'desk', Component: DeskLayoutRoute, children: childrenOf('desk', true) },
                { path: 'ops', Component: OpsLayoutRoute, children: childrenOf('ops', true) },
                {
                    path: 'comply',
                    Component: ComplyLayoutRoute,
                    children: childrenOf('comply', true),
                },
                { path: 'view', Component: ViewLayoutRoute, children: childrenOf('view', true) },
                {
                    // The Admin shell: firm administration, for a user who
                    // holds the `admin` token (ADR-0004).
                    path: 'admin',
                    Component: AdminLayoutRoute,
                    children: childrenOf('admin', true),
                },
                {
                    // Client portal — its own chrome (PortalLayoutRoute), gated on a
                    // portal credential session, no staff sidebar/agent panel.
                    path: 'portal',
                    Component: PortalLayoutRoute,
                    children: childrenOf('portal', false),
                },
            ],
        },
    ],
    { basename },
);
